import { create } from 'zustand';
import { getAutoRefills, putAutoRefills } from '../services/accountApi';
import { getIsImpersonating, type SessionUser } from '../utils/session';
import type { AutoRefillPrescription, AutoRefillUser } from '../types/api';

// ─── autoFillCount helper ────────────────────────────────────────────────────

function computeAutoFillCount(prescriptions: AutoRefillPrescription[]): number {
  return prescriptions.filter(p => p.IsAutoRefillEnabled === true).length;
}

// ─── Store interface ─────────────────────────────────────────────────────────

export interface AutoRefillStore {
  // User list & selection
  refillUsers: AutoRefillUser[];
  selectedLinkToken: string | null;

  // Fetched prescription data
  prescriptions: AutoRefillPrescription[];
  isPatientAutoRefillEnabled: boolean;
  autoRefillUsername: string;
  linkToken: string;

  // UI / computed
  view: 'List' | 'Grid';
  loading: boolean;
  saving: boolean;
  agreeToTerms: boolean;
  noAutoFills: boolean;
  autoFillCount: number;

  // Feedback
  error: string | null;
  saveSuccess: boolean;

  // Actions
  loadUsers: (user: SessionUser) => void;
  setSelectedUser: (linkToken: string) => void;
  setView: (view: 'List' | 'Grid') => void;
  fetchAutoRefills: (linkToken: string) => Promise<void>;
  togglePrescription: (scriptNo: string, enabled: boolean) => void;
  setPatientEnabled: (enabled: boolean) => void;
  setAgreeToTerms: (agreed: boolean) => void;
  saveAutoRefills: () => Promise<void>;
  reset: () => void;
}

// ─── Initial state ───────────────────────────────────────────────────────────

const initialState: Omit<
  AutoRefillStore,
  | 'loadUsers'
  | 'setSelectedUser'
  | 'setView'
  | 'fetchAutoRefills'
  | 'togglePrescription'
  | 'setPatientEnabled'
  | 'setAgreeToTerms'
  | 'saveAutoRefills'
  | 'reset'
> = {
  refillUsers: [],
  selectedLinkToken: null,
  prescriptions: [],
  isPatientAutoRefillEnabled: false,
  autoRefillUsername: '',
  linkToken: '',
  view: 'List',
  loading: false,
  saving: false,
  agreeToTerms: false,
  noAutoFills: false,
  autoFillCount: 0,
  error: null,
  saveSuccess: false,
};

// ─── Store ───────────────────────────────────────────────────────────────────

export const useAutoRefillStore = create<AutoRefillStore>((set, get) => ({
  ...initialState,

  loadUsers(user: SessionUser) {
    const dependants = Array.isArray(user.Dependants) ? user.Dependants : [];

    const mappedDependants: AutoRefillUser[] = (dependants as Array<Record<string, unknown>>)
      .filter(
        d =>
          d.DependantClass !== 'BLOCKED_BY_PATIENT' && d.DependantClass !== 'PATIENT_NEEDS_ACCOUNT'
      )
      .map(d => ({
        Name: `${String(d.FirstName ?? '')} ${String(d.LastName ?? '')}`.trim(),
        LinkToken: String(d.LinkToken ?? ''),
        DependantClass: typeof d.DependantClass === 'string' ? d.DependantClass : undefined,
      }));

    const primaryUser: AutoRefillUser = {
      Name: `${user.FirstName ?? ''} ${user.LastName ?? ''}`.trim(),
      LinkToken: String(user.LinkToken ?? ''),
    };

    const refillUsers = [primaryUser, ...mappedDependants];

    set({ refillUsers });
  },

  setSelectedUser(linkToken: string) {
    set({
      selectedLinkToken: linkToken,
      prescriptions: [],
      saveSuccess: false,
      error: null,
    });
  },

  setView(view: 'List' | 'Grid') {
    set({ view });
  },

  async fetchAutoRefills(linkToken: string) {
    set({ loading: true, error: null });
    try {
      const data = await getAutoRefills(linkToken);

      const prescriptions = data.Prescriptions ?? null;
      const noAutoFills = prescriptions === null || prescriptions.length === 0;
      const normalizedPrescriptions = prescriptions ?? [];
      const autoFillCount = computeAutoFillCount(normalizedPrescriptions);

      set({
        loading: false,
        prescriptions: normalizedPrescriptions,
        linkToken: data.LinkToken ?? linkToken,
        isPatientAutoRefillEnabled: data.IsPatientAutoRefillEnabled ?? false,
        autoRefillUsername: `${data.FirstName ?? ''} ${data.LastName ?? ''}`.trim(),
        noAutoFills,
        autoFillCount,
        agreeToTerms: false,
      });
    } catch (err) {
      const message =
        typeof err === 'object' &&
        err !== null &&
        typeof (err as { message?: unknown }).message === 'string'
          ? (err as { message: string }).message
          : 'Failed to load auto-refill data.';
      set({ loading: false, error: message });
    }
  },

  togglePrescription(scriptNo: string, enabled: boolean) {
    const prescriptions = get().prescriptions.map(p =>
      p.ScriptNo === scriptNo ? { ...p, IsAutoRefillEnabled: enabled } : p
    );
    set({ prescriptions, autoFillCount: computeAutoFillCount(prescriptions) });
  },

  setPatientEnabled(enabled: boolean) {
    set({
      isPatientAutoRefillEnabled: enabled,
      agreeToTerms: enabled ? get().agreeToTerms : false,
    });
  },

  setAgreeToTerms(agreed: boolean) {
    set({ agreeToTerms: agreed });
  },

  async saveAutoRefills() {
    const state = get();

    if (state.isPatientAutoRefillEnabled && !state.agreeToTerms) {
      return;
    }

    if (getIsImpersonating()) {
      return;
    }

    set({ saving: true, error: null, saveSuccess: false });
    try {
      const response = await putAutoRefills(state.linkToken, {
        IsPatientEnabled: state.isPatientAutoRefillEnabled,
        Prescriptions: state.prescriptions,
      });

      if (response.StatusCode === 'SUCCESS') {
        set({ saving: false, saveSuccess: true });
        await get().fetchAutoRefills(state.linkToken);
      } else {
        set({
          saving: false,
          error: response.StatusMessage ?? 'Failed to save auto-refill settings.',
        });
      }
    } catch (err) {
      const message =
        typeof err === 'object' &&
        err !== null &&
        typeof (err as { message?: unknown }).message === 'string'
          ? (err as { message: string }).message
          : 'Failed to save auto-refill settings.';
      set({ saving: false, error: message });
    }
  },

  reset() {
    set({ ...initialState });
  },
}));
